import type { RxCollection } from 'rxdb';

import type { RxDatabaseCollections, SchemaType, Tag } from '@flintmint/core-shared/rxdb';
import { searchService } from '@/services/search-service';
import type { OramaSearchParams } from '@/services/search-service/types';

/**
 * Detaches a tag from all docs that reference it. Used both when a tag is deleted and by
 * "Detach From Everything". Orama caps an unbounded search at its default page size, so the
 * sweep re-queries with `limit: count` whenever the first page is truncated.
 * @param rxCollections - RxDatabase collections
 * @param tagId - ID of the tag to detach
 */
export const detachTagFromDocs = async (rxCollections: RxDatabaseCollections, tagId: string) => {
  // containsAny for single-tag membership lookup (OR semantics correct here; useSearch uses containsAll for AND filtering)
  const searchParams: OramaSearchParams = { where: { tagIds: { containsAny: [tagId] } } };

  const firstPage = await searchService.search(searchParams);
  const response =
    firstPage.count > firstPage.hits.length ? await searchService.search({ ...searchParams, limit: firstPage.count }) : firstPage;

  const updatePromises = response.hits.map(async (searchResult) => {
    const searchableDoc = searchResult.document;

    const collectionType = searchableDoc.type === 'templates' ? 'notes' : (searchableDoc.type as SchemaType);
    const collection = rxCollections[collectionType];
    if (!collection) {
      console.error('detachTagFromDocs: invalid collection type', collectionType);
      return;
    }

    const rxDoc = await collection.findOne(searchableDoc.id).exec();
    if (!rxDoc) {
      console.error('detachTagFromDocs: rxDoc not found', collectionType, searchableDoc.id);
      return;
    }

    return rxDoc.incrementalUpdate({ $pullAll: { tagIds: [tagId] } });
  });

  return Promise.all(updatePromises);
};

/**
 * Updates tag numDocs after a tagged doc gets saved
 * @param tagsCollection - tags RxCollection
 * @param oldTagIds - doc tagIds before save
 * @param newTagIds - doc tagIds after save
 */
export const updateTagNumDocs = async (
  tagsCollection: RxCollection<Tag>,
  oldTagIds: string[] = [],
  newTagIds: string[] = []
): Promise<void> => {
  const oldSet = new Set(oldTagIds || []);
  const newSet = new Set(newTagIds || []);

  const removedTagIds = [...oldSet].filter((id) => !newSet.has(id));
  const addedTagIds = [...newSet].filter((id) => !oldSet.has(id));

  await Promise.all([decrTagNumDocs(tagsCollection, removedTagIds), incrTagNumDocs(tagsCollection, addedTagIds)]);
};

// numDocs bookkeeping: the two helpers below apply +1/-1 locally on every device, unclamped. Do
// not "fix" a mismatch here by rewriting numDocs to an absolute value — that reintroduces
// last-writer-wins drift (DM-1463). See docs/tag-counts/tag-counts-architecture.md for the counter contract.
/**
 * Increments the numDocs count for the specified tags
 * @param tagsCollection - RxCollection of tags
 * @param tagIds - Array of tag IDs to increment (can contain duplicates for batch operations)
 */
export const incrTagNumDocs = async (tagsCollection: RxCollection<Tag>, tagIds?: string[]): Promise<any> => {
  if (!tagIds?.length) return;

  // Count occurrences of each tag
  const tagCountMap = tagIds.reduce<Record<string, number>>((acc, tagId) => {
    acc[tagId] = (acc[tagId] || 0) + 1;
    return acc;
  }, {});

  const updatePromises = Object.entries(tagCountMap).map(([tagId, count]) => findTagIncrNumDocs(tagsCollection, tagId, count));
  return Promise.all(updatePromises);
};

const findTagIncrNumDocs = async (tagsCollection: RxCollection<Tag>, tagId: string, count: number): Promise<any> => {
  const tag = await tagsCollection.findOne(tagId).exec();
  if (tag) {
    return tag.incrementalUpdate({
      $inc: { numDocs: count },
    });
  }
};

/**
 * Decrements the numDocs count for the specified tags
 * @param tagsCollection - RxCollection of tags
 * @param tagIds - Array of tag IDs to decrement (can contain duplicates for batch operations)
 */
export const decrTagNumDocs = async (tagsCollection: RxCollection<Tag>, tagIds?: string[]): Promise<any> => {
  if (!tagIds?.length) return;

  // Count occurrences of each tag
  const tagCountMap = tagIds.reduce<Record<string, number>>((acc, tagId) => {
    acc[tagId] = (acc[tagId] || 0) + 1;
    return acc;
  }, {});

  const updatePromises = Object.entries(tagCountMap).map(([tagId, count]) => findTagDecrNumDocs(tagsCollection, tagId, count));
  return Promise.all(updatePromises);
};

const findTagDecrNumDocs = async (tagsCollection: RxCollection<Tag>, tagId: string, count: number): Promise<any> => {
  const tag = await tagsCollection.findOne(tagId).exec();
  if (!tag) return;

  // Deliberately unclamped: clamping here would truncate the local delta before tagConflictHandler
  // can merge it, losing it permanently (DM-1463).
  return tag.incrementalModify((docData) => ({
    ...docData,
    numDocs: (docData.numDocs ?? 0) - count,
  }));
};
